import { useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import {
  useAdminCollection,
  createAdminDoc,
  updateAdminDoc,
  deleteAdminDoc,
} from '@/hooks/useAdminCollection';
import { AdminNote, timestampToDate } from '@/types';

const COLLECTION = 'notes';

interface NoteForm {
  title: string;
  body: string;
  tags: string;
  pinned: boolean;
}

const EMPTY_FORM: NoteForm = { title: '', body: '', tags: '', pinned: false };

function NotesContent() {
  const { currentUser } = useAuth();
  const { items: notes, loading } = useAdminCollection<AdminNote>(COLLECTION, 'updatedAt');

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AdminNote | null>(null);
  const [form, setForm] = useState<NoteForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = s
      ? notes.filter(
          (n) =>
            n.title.toLowerCase().includes(s) ||
            n.body.toLowerCase().includes(s) ||
            (n.tags || []).some((t) => t.toLowerCase().includes(s)),
        )
      : notes;
    // Pinned first, otherwise keep the updatedAt-desc order from the query.
    return [...list].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned));
  }, [notes, search]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const openEdit = (note: AdminNote) => {
    setEditing(note);
    setForm({
      title: note.title,
      body: note.body,
      tags: (note.tags || []).join(', '),
      pinned: !!note.pinned,
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const tags = form.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        tags,
        pinned: form.pinned,
      };
      if (editing) {
        await updateAdminDoc(COLLECTION, editing.id, payload);
      } else {
        await createAdminDoc(COLLECTION, payload, currentUser!.uid);
      }
      setShowForm(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const togglePin = async (note: AdminNote) => {
    try {
      await updateAdminDoc(COLLECTION, note.id, { pinned: !note.pinned });
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleDelete = async (note: AdminNote) => {
    if (!window.confirm(`Delete note "${note.title}"?`)) return;
    try {
      await deleteAdminDoc(COLLECTION, note.id);
    } catch (err) {
      console.error('Failed to delete note:', err);
    }
  };

  return (
    <div style={{ padding: '24px 0' }}>
      <div className="terminal-card">
        <div className="card-header">
          <span className="card-title">Notes &amp; Docs</span>
          <div className="card-header-right">
            <button className="action-btn large" onClick={openAdd}>+ New Note</button>
          </div>
        </div>
        <div className="card-body">
          <input
            className="terminal-input"
            placeholder="Search notes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: '320px' }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--muted)', fontSize: '11px', letterSpacing: '0.12em' }}>
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--muted)', fontSize: '12px' }}>
          {notes.length === 0 ? 'No notes yet. Create your first note.' : 'No notes match your search.'}
        </div>
      ) : (
        <div className="notes-grid">
          {filtered.map((note) => (
            <div key={note.id} className={`note-card${note.pinned ? ' note-card--pinned' : ''}`}>
              <div className="note-card__head">
                <span className="note-card__title">{note.title}</span>
                <button
                  className={`note-pin${note.pinned ? ' note-pin--on' : ''}`}
                  onClick={() => togglePin(note)}
                  title={note.pinned ? 'Unpin' : 'Pin'}
                >
                  ★
                </button>
              </div>
              {note.body && <p className="note-card__body">{note.body}</p>}
              {(note.tags || []).length > 0 && (
                <div className="note-card__tags">
                  {note.tags!.map((t) => (
                    <span key={t} className="note-tag">{t}</span>
                  ))}
                </div>
              )}
              <div className="note-card__foot">
                <span className="note-card__date">
                  {timestampToDate(note.updatedAt)?.toLocaleDateString() || ''}
                </span>
                <div>
                  <button className="action-btn secondary crm-mini-btn" onClick={() => openEdit(note)}>Edit</button>
                  <button className="action-btn danger crm-mini-btn" onClick={() => handleDelete(note)}>Del</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="terminal-card" style={{ maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="card-header">
              <span className="card-title">{editing ? 'Edit Note' : 'New Note'}</span>
            </div>
            <form className="card-body" onSubmit={handleSave}>
              {error && <div className="terminal-alert error">Error: {error}</div>}
              <div className="field-group">
                <label className="field-label">Title *</label>
                <input className="terminal-input" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Note title" autoFocus />
              </div>
              <div className="field-group">
                <label className="field-label">Body</label>
                <textarea className="terminal-textarea" style={{ minHeight: '160px' }} value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Write your note…" />
              </div>
              <div className="field-group">
                <label className="field-label">Tags (comma-separated)</label>
                <input className="terminal-input" value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="ops, ideas, follow-up" />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: 'var(--text-2)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.pinned}
                  onChange={(e) => setForm({ ...form, pinned: e.target.checked })} />
                Pin to top
              </label>
              <div className="btn-row">
                <button type="submit" className="action-btn large" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Note'}
                </button>
                <button type="button" className="action-btn secondary" onClick={() => setShowForm(false)} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminNotesPage() {
  return (
    <ProtectedRoute requireAdmin>
      <Layout wide>
        <NotesContent />
      </Layout>
    </ProtectedRoute>
  );
}
